import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MONO, eur, eurS } from './format'
import type { EditorialMover, MoverFilter } from '../types'

interface EditorialMoversProps {
  movers: EditorialMover[]
  filter: MoverFilter
  /** Header labels for the "what drove it" table (previous vs current month). */
  priorLabel: string
  currLabel: string
}

/** Curated "Where the money moved" — renders the source dashboard's movers
 *  verbatim (tag, headline, explanation, source line, signed value), filterable
 *  to All / Declines / Gains. Each row expands into a "what drove it" table
 *  comparing the current month to the previous one. */
export function EditorialMovers({ movers, filter, priorLabel, currLabel }: EditorialMoversProps) {
  const [open, setOpen] = useState<number | null>(null)
  const rows = movers
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => filter === 'All' || (filter === 'Declines' ? m.dir === 'decline' : m.dir === 'gain'))
  if (!rows.length) {
    return <p className="py-6 text-center text-xs text-stone-400">No {filter.toLowerCase()} this period.</p>
  }
  return (
    <div className="divide-y divide-stone-100 dark:divide-stone-800">
      {rows.map(({ m, i }) => {
        const decline = m.dir === 'decline'
        const isOpen = open === i
        const hasLines = !!m.lines?.length
        return (
          <div
            key={i}
            className={cn(
              'border-l-2 transition-colors',
              decline ? 'border-red-400 dark:border-red-500/60' : 'border-emerald-400 dark:border-emerald-500/60',
              isOpen ? 'bg-[#F4EDDD] dark:bg-champagne-500/10' : hasLines ? 'hover:bg-[#F4EDDD] dark:hover:bg-champagne-500/10' : '',
            )}
          >
            <button
              type="button"
              onClick={() => hasLines && setOpen(isOpen ? null : i)}
              aria-expanded={isOpen}
              className={cn('flex w-full items-start gap-3 px-4 py-3 text-left', !hasLines && 'cursor-default')}
            >
              <span className={cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', decline ? 'bg-red-500' : 'bg-emerald-500')} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:bg-stone-800 dark:text-stone-400">
                    {m.tag}
                  </span>
                  <span className="text-sm font-medium text-stone-800 dark:text-stone-100">{m.title}</span>
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-stone-500 dark:text-stone-400">{m.desc}</p>
                <p className="mt-1 text-[11px] font-medium uppercase tracking-wider text-stone-400">{m.src}</p>
              </div>
              <span
                className={cn(
                  'shrink-0 text-sm font-semibold',
                  MONO,
                  decline ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400',
                )}
              >
                {m.val}
              </span>
              {hasLines && (
                <ChevronDown className={cn('mt-0.5 h-4 w-4 shrink-0 text-stone-400 transition-transform', isOpen && 'rotate-180')} />
              )}
            </button>

            {isOpen && hasLines && (
              <div className="px-4 pb-3 sm:pl-9">
                {/* Mobile: stacked cards — the 4-column table collides at narrow widths */}
                <div className="space-y-2 sm:hidden">
                  {m.lines!.map((l, li) => {
                    const dlt = l.curr - l.prev
                    const up = dlt >= 0
                    return (
                      <div key={li} className="rounded-lg border border-stone-100 bg-stone-50/50 p-2.5 dark:border-stone-800 dark:bg-stone-800/30">
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-sm font-medium text-stone-700 dark:text-stone-200">{l.name}</span>
                          <span className={cn('shrink-0 text-sm font-semibold', MONO, up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                            {dlt === 0 ? '—' : eurS(dlt)}
                          </span>
                        </div>
                        <div className="mt-2 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-xs">
                          <span className="text-stone-400">{priorLabel}</span>
                          <span className={cn('text-right text-stone-500 dark:text-stone-400', MONO)}>{eur(l.prev)}</span>
                          <span className="text-stone-400">{currLabel}</span>
                          <span className={cn('text-right text-stone-700 dark:text-stone-200', MONO)}>{eur(l.curr)}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {/* Desktop: full comparison table */}
                <table className="hidden w-full text-sm sm:table">
                  <thead>
                    <tr className="border-y border-stone-100 text-[10px] uppercase tracking-wider text-stone-400 dark:border-stone-800">
                      <th className="py-1.5 text-left font-medium">What drove it</th>
                      <th className="py-1.5 text-right font-medium">{priorLabel}</th>
                      <th className="py-1.5 text-right font-medium">{currLabel}</th>
                      <th className="py-1.5 text-right font-medium">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.lines!.map((l, li) => {
                      const dlt = l.curr - l.prev
                      const up = dlt >= 0
                      return (
                        <tr key={li} className="border-b border-stone-50 last:border-0 hover:bg-champagne-50/60 dark:border-stone-800/60 dark:hover:bg-champagne-500/10">
                          <td className="py-1.5 pr-3 text-stone-700 dark:text-stone-200">{l.name}</td>
                          <td className={cn('py-1.5 text-right text-stone-400', MONO)}>{eur(l.prev)}</td>
                          <td className={cn('py-1.5 text-right text-stone-600 dark:text-stone-300', MONO)}>{eur(l.curr)}</td>
                          <td className={cn('py-1.5 text-right font-medium', MONO, up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                            {dlt === 0 ? '—' : eurS(dlt)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
